import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './NavPillTabs.css';

/**
 * NavPillTabs — the HMS pill tab bar.
 *
 * Pills sit in a recessed rounded tray. Each pill carries a small tinted
 * icon tile; the active pill expands and gets the shared brand gradient,
 * inactive pills collapse to icon-only and expand on hover. Counts render
 * as a corner badge on the tile rather than "(3)" in the label.
 *
 * Controlled component:
 *   items: [{
 *     key        string, tab id
 *     label      string
 *     icon       FontAwesome icon object
 *     tint       CSS color for the icon tile (per-tab identity)
 *     count      optional number badge (0 renders as a badge showing 0
 *                only when showZero is true on the item)
 *     showZero   optional bool, default false
 *     dataTour   optional string, forwarded as data-tour
 *     className  optional extra pill classes (e.g. 'has-birthday')
 *   }]
 *   activeKey: currently active item key
 *   onChange: (key) => void
 *   alwaysLabel: keep every label out instead of collapsing inactive pills
 *                to their icon. For trays that select DATA rather than
 *                pages — one pill per legal entity, say — where every item
 *                carries the same icon, so the collapsed rest state leaves
 *                the user a row of identical tiles with nothing to read.
 */
const NavPillTabs = ({ items, activeKey, onChange, alwaysLabel = false, className = '' }) => (
  <div
    className={`nav-pill-tray ${alwaysLabel ? 'nav-pill-tray--labelled' : ''} ${className}`}
    role="tablist"
  >
    {items.map((item) => {
      const isActive = item.key === activeKey;
      const showBadge = item.count != null && (item.count > 0 || item.showZero);
      return (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={isActive}
          className={`nav-pill ${isActive ? 'active' : ''} ${item.className || ''}`}
          onClick={() => onChange(item.key)}
          title={item.label}
          {...(item.dataTour ? { 'data-tour': item.dataTour } : {})}
        >
          <span className="nav-pill-tile" style={{ '--pill-tint': item.tint || 'var(--color-brand)' }}>
            <FontAwesomeIcon icon={item.icon} />
            {showBadge && (
              <span className="nav-pill-badge">{item.count > 99 ? '99+' : item.count}</span>
            )}
          </span>
          <span className="nav-pill-label">{item.label}</span>
        </button>
      );
    })}
  </div>
);

export default NavPillTabs;
